/* global window */
import { WpApi } from '../utils/WpApi'


/**
 * Class for shared functionality between property objects (e.g. single listing and property blocks)
 *
 */
export class PropertySkeleton {


	// HTML elements
	node
	nodeFavoriteBtn
	nodeError

	errorTimeout

	// grabbed from window/set through Assets class
	addonData = {}

	// states
	isFavorited = false


	/**
	 * Constructor
	 *
	 * @param {HTMLElement} node Base node element
	 */
	constructor( node ) {
		this.node = node
		this.nodeFavoriteBtn = this.node.querySelector( '.js-property-favorite-btn' )
		this.nodeError = this.node.querySelector( '.js-property-error' )

		this.isFavorited = this.node.classList.contains( 'is-favorited' )

		this.addonData = window.webfxPropertyManagerData
	}
		
		
	/**
	 * Initialize basic class functions
	 *
	 * @return {void}
	 */
	init() {
		this.initEventListeners()
	}


	/**
	 * Attach event listeners
	 * 
	 * @todo abstract out with property block (same functionality)
	 *
	 * @return {void}
	 */
	initEventListeners() {

		// add/remove property from favorites (only for logged in users)
		if( this.nodeFavoriteBtn ) {
			this.nodeFavoriteBtn.addEventListener( 'click', this.handleFavoriteBtnClick.bind( this ) )
		}
	}


	/**
	 * Handle user clicking on favorite button
	 * 
	 * Button is only visible for logged in users
	 *
	 * @return {void}
	 */
	async handleFavoriteBtnClick() {
		this.nodeFavoriteBtn.classList.add( 'is-loading' )

		if( this.isFavorited ) {
			this.removeFavoriteStatus()
		} else {
			this.addFavoriteStatus()
		}

		this.nodeFavoriteBtn.classList.remove( 'is-loading' )
	}


	/**
	 * Remove favorite status from property (and remove from user account's favorited properties)
	 *
	 * @return {void}
	 */
	async removeFavoriteStatus() {
		const userId = window?.webfxPropertyManagerData?.currentUserId
		const propertyPostId = this.node.dataset.propertyPostId
		
		try {
			if( !userId ) {
				throw new Error( 'Invalid user ID' )
			}

			if( !propertyPostId ) {
				throw new Error( 'Invalid property post ID' )
			}

			await WpApi.sendReq({
				userId,
				propertyId: propertyPostId
			}, 'v1/unfavorite', 'POST' )

			this.node.classList.remove( 'is-favorited' )
			this.isFavorited = false

		} catch ( err ) {
			console.warn( err )

			this.showErrorMsg( 'Failed to add to favorites. Please try again.' )
		}
	}


	/**
	 * Add favorite status to property (and add to user account's favorited properties)
	 *
	 * @return {void}
	 */
	async addFavoriteStatus() {
		const userId = window?.webfxPropertyManagerData?.currentUserId
		const propertyPostId = this.node.dataset.propertyPostId
		
		try {
			if( !userId ) {
				throw new Error( 'Invalid user ID' )
			}
	
			if( !propertyPostId ) {
				throw new Error( 'Invalid property post ID' )
			}

			// Add the loading class before the request
			this.node.classList.add( 'is-loading' );

			await WpApi.sendReq({
				userId,
				propertyId: propertyPostId
			}, 'v1/favorite', 'POST' )

			// Ensure the loading class is applied for at least 1 second
			setTimeout( () => {
				// Remove the loading class after the request
				this.node.classList.remove( 'is-loading' )
				this.node.classList.add( 'is-favorited' )
				this.isFavorited = true
			}, 1500 )

		} catch ( err ) {
			console.warn( err )

			this.showErrorMsg( 'Failed to add to favorites. Please try again.' )
		}
	}


	/**
	 * Show error message
	 *
	 * @param {string} msg Error message
	 * @return {void}
	 */
	showErrorMsg( msg = '' ) {
		clearTimeout( this.errorTimeout )

		if( !msg ) {
			this.hideErrorMsg()
		}

		this.nodeError.innerText = msg
		this.nodeError.classList.add( 'is-visible' )

		this.errorTimeout = setTimeout( () => {
			this.hideErrorMsg()
		}, 5000 )
	}


	/**
	 * Hide error message
	 *
	 * @return {void}
	 */
	hideErrorMsg() {
		this.nodeError.innerText = ''
		this.nodeError.classList.remove( 'is-visible' )
	}

}
